<ng-container *ngIf="isSectionVisible">
  <ind-section-header *ngIf="showSectionHeader; else plainTitle" [viewData]="sectionHeader"></ind-section-header>
  <ng-template #plainTitle>
    <div class="sdr-notes-plain-title">
      {{ sectionHeader?.title }}<span class="sdr-notes-mandatory-asterisk" *ngIf="sectionHeader?.isRequired">*</span>
    </div>
  </ng-template>

  <!-- Composer (hidden in read-only mode). Which inputs appear is driven by the
       BU config: note text (showNoteInput) and/or the attach affordance
       (isAttachmentEnabled). Attach + Add note now live as a footer row INSIDE
       the composer card (not the section header) so the card reads as one
       self-contained unit, matching the reference design. -->
  <div class="sdr-notes-input" *ngIf="!readOnly">
    <div class="sdr-notes-composer-card">
      <!-- Note text input — notes / notes+attachments modes. The staged file
           (if any) renders below via our own icon-square row, not
           ind-text-area's built-in chip (a plain text label with no icon) —
           so it looks the same everywhere a file is staged, regardless of mode. -->
      <ind-text-area
        *ngIf="showNoteInput"
        id="sdr-new-note"
        [placeHolder]="notesPlaceholder"
        [value]="newNoteText"
        [maxLength]="1000"
        [rows]="4"
        [cols]="10"
        (valueChange)="onNoteTextChange($event)">
      </ind-text-area>

      <!-- Staged file row — shown whenever attachments are enabled and a file is
           staged, in both attachments-only and notes+attachments modes. Only one
           file may be staged at a time (Attach replaces, not appends). -->
      <div class="sdr-notes-attachment-chip" *ngIf="isAttachmentEnabled && stagedAttachment">
        <div class="sdr-notes-attachment-info">
          <span class="sdr-notes-attachment-name">{{ attachmentTitle }}</span>
          <span class="sdr-notes-attachment-meta">{{ attachmentSizeLabel }} · {{ 'READY_TO_UPLOAD' | translate }}</span>
        </div>
        <ion-icon name="close-outline" class="sdr-notes-attachment-remove" (click)="removeStagedAttachment()"></ion-icon>
      </div>

      <!-- Notes / notes+attachments modes: Attach + Add note footer row.
           Icon-only (no text labels) — attach (paperclip) + send. -->
      <div class="sdr-notes-composer-footer" *ngIf="showNoteInput">
        <button
            *ngIf="isAttachmentEnabled"
            type="button"
            class="sdr-notes-attach-link sdr-notes-attach-link--icon"
            [attr.aria-label]="'ATTACH' | translate"
            (click)="triggerAttachPicker()">
          <ion-icon name="attach-outline"></ion-icon>
        </button>
        <span class="sdr-notes-composer-footer-spacer"></span>
        <button
            type="button"
            class="sdr-notes-send-btn"
            [disabled]="isSaveDisabled"
            [attr.aria-label]="'ADD_NOTE' | translate"
            (click)="saveNewNote()">
          <ion-icon name="send"></ion-icon>
        </button>
      </div>

      <!-- Attachments-only mode: no Attach/Add note — a single dashed "+ Add
           file" affordance instead, hidden once a file is staged (single-file
           limit: remove the current one via the row's × before adding another). -->
      <button
          *ngIf="!showNoteInput && isAttachmentEnabled && !stagedAttachment"
          type="button"
          class="sdr-notes-add-file-btn"
          (click)="triggerAttachPicker()">
        <span class="sdr-notes-add-file-icon">+</span>
        <span>{{ 'ADD_FILE' | translate }}</span>
      </button>
    </div>

    <!-- Hidden file input, triggered by the footer Attach button. `cancel`
         fires when the picker is dismissed without a file — only then does the
         attachment-required helper surface (opening the picker alone does not). -->
    <input class="sdr-notes-file-input" #attachInput type="file" (change)="loadImageFromDevice($event)" (cancel)="onAttachPickerCancel()">

    <!-- Mandatory helper — only after the user engages a still-unmet required section -->
    <div class="sdr-section-error sdr-section-error--inline" *ngIf="isNotesInvalid">
      {{ inlineRequiredMessageKey | translate }}
    </div>
  </div>

  <!-- Notes staged in this session (create flow). Same card design as the
       read-only entries below (avatar + owner/timestamp + text + attachment
       row), plus Edit/Delete icon controls since these aren't submitted yet.
       They live only in memory until SDR submit, where `getStagedNotes()`
       flushes them as part of the batch. -->
  <div class="sdr-note-entry" *ngFor="let note of stagedNotesView; let i = index; trackBy: trackByIndex">
    <div class="sdr-note-entry-header">
      <div class="sdr-note-entry-meta">
        <span class="sdr-note-owner">{{ note.ownerName }}</span>
        <span class="sdr-note-timestamp">{{ getFormattedDateTime(note.createdTime) }}</span>
      </div>
      <div class="sdr-note-entry-actions">
        <ion-icon
            *ngIf="showNoteInput && editingStagedIndex !== i"
            name="create-outline"
            class="sdr-note-action-icon"
            (click)="startEditStagedNote(i)">
        </ion-icon>
        <ion-icon
            name="trash-outline"
            class="sdr-note-action-icon sdr-note-action-icon--danger"
            (click)="deleteStagedNote(i)">
        </ion-icon>
      </div>
    </div>

    <!-- Editing this staged note's text AND attachment (pencil icon above) -->
    <ng-container *ngIf="editingStagedIndex === i; else stagedTextView">
      <ind-text-area
          [value]="editingStagedText"
          [maxLength]="1000"
          [rows]="3"
          [cols]="10"
          (valueChange)="editingStagedText = $event">
      </ind-text-area>

      <div class="sdr-notes-attachment-chip" *ngIf="isAttachmentEnabled && editingAttachmentView(note) as att">
        <div class="sdr-notes-attachment-info">
          <span class="sdr-notes-attachment-name">{{ att.name }}</span>
          <span class="sdr-notes-attachment-meta">{{ att.sizeLabel }}</span>
        </div>
        <ion-icon name="close-outline" class="sdr-notes-attachment-remove" (click)="removeEditingAttachment()"></ion-icon>
      </div>

      <input class="sdr-notes-file-input" #editAttachInput type="file" (change)="loadEditImageFromDevice($event)">

      <div class="sdr-note-edit-actions">
        <button
            *ngIf="isAttachmentEnabled"
            type="button"
            class="sdr-notes-attach-link sdr-notes-attach-link--icon"
            [attr.aria-label]="'ATTACH' | translate"
            (click)="triggerEditAttachPicker()">
          <ion-icon name="attach-outline"></ion-icon>
        </button>
        <span class="sdr-notes-composer-footer-spacer"></span>
        <button type="button" class="sdr-notes-attach-link" (click)="cancelEditStagedNote()">{{ 'CANCEL' | translate }}</button>
        <button type="button" class="sdr-notes-add-btn" (click)="saveEditStagedNote(i)">{{ 'SAVE' | translate }}</button>
      </div>
    </ng-container>
    <ng-template #stagedTextView>
      <p class="sdr-note-text" *ngIf="note.noteText">{{ note.noteText }}</p>
    </ng-template>

    <div
        class="sdr-note-attachment-pill"
        *ngIf="isAttachmentEnabled && note.hasDocument && editingStagedIndex !== i"
        (click)="downloadNoteAttachment(note)">
      <span class="sdr-note-attachment-pill-name">{{ note.documentName }}</span>
      <ion-icon name="download-outline" class="sdr-note-attachment-pill-download"></ion-icon>
    </div>
  </div>

  <!-- Existing notes list. On a reopened (submitted) SDR the section is
       readOnly, so Edit + Delete are suppressed — a submitted SDR's notes are
       view-only (OMNI-57234). Edit/Delete remain available only on the
       editable (create) surface, which renders staged notes above. A
       dedicated card (avatar + owner/timestamp + note text + attachment row)
       is used here instead of <io-note> — see the "Read-only entry display"
       block in the .ts for why. -->
  <div class="sdr-note-entry" *ngFor="let note of existingNotes; trackBy: trackByNoteId">
    <div class="sdr-note-entry-header">
      <div class="sdr-note-entry-meta">
        <span class="sdr-note-owner">{{ note.ownerName }}</span>
        <span class="sdr-note-timestamp">{{ getFormattedDateTime(note.createdTime) }}</span>
      </div>
      <span class="sdr-note-file-only-badge" *ngIf="isFileOnlyNote(note)">{{ 'FILE_ONLY' | translate }}</span>
    </div>
    <p class="sdr-note-text" *ngIf="note.noteText">{{ note.noteText }}</p>
    <div
        class="sdr-note-attachment-pill"
        *ngIf="isAttachmentEnabled && note.hasDocument"
        (click)="downloadNoteAttachment(note)">
      <span class="sdr-note-attachment-pill-name">{{ note.documentName }}</span>
      <ion-icon name="download-outline" class="sdr-note-attachment-pill-download"></ion-icon>
    </div>
  </div>
</ng-container>
